import React, { useState, useEffect } from "react";
import { Box, Container, Typography, IconButton } from "@mui/material";
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from "@mui/icons-material";

// Import all 8 hardware images
import img1 from "../../../images/hardware/collage 2.png";
import img2 from "../../../images/hardware/camview1.png";
import img3 from "../../../images/hardware/camview2.png";
import img4 from "../../../images/hardware/camview3.png";
import img5 from "../../../images/hardware/camview4.png";
import img6 from "../../../images/hardware/camview5.png";
import img7 from "../../../images/hardware/camview6.png";
import img8 from "../../../images/hardware/collage 3.png";

export default function Hardware() {
  const images = [img1, img2, img3, img4, img5, img6, img7, img8];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-slide every 4 seconds
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isHovered, images.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const getPrevIndex = (index) => (index - 1 + images.length) % images.length;
  const getNextIndex = (index) => (index + 1) % images.length;

  return (
    <Box
      sx={{
        py: 8,
        background: "linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)",
        position: "relative",
        overflow: "hidden",
      }}
      id="hardware"
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          fontWeight={700}
          gutterBottom
          sx={{
            textAlign: "center",
            mb: 2,
            color: "#fff",
            fontSize: { xs: "2rem", md: "3rem" },
          }}
        >
          Hardware Gallery
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            mb: 6,
            color: "rgba(255,255,255,0.7)",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          Explore our advanced camera trap devices in action
        </Typography>

        {/* ================= SLIDER ================= */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 900,
            mx: "auto",
            height: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* PREV IMAGE (left small) */}
          <Box
            sx={{
              position: "absolute",
              left: "5%",
              width: 180,
              height: 260,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
              transform: "scale(0.8) perspective(1000px) rotateY(15deg)",
              opacity: 0.6,
              transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: 1,
              filter: "brightness(0.7)",
              "&:hover": {
                transform: "scale(0.85) perspective(1000px) rotateY(10deg)",
                opacity: 0.8,
              },
            }}
          >
            <Box
              component="img"
              src={images[getPrevIndex(currentIndex)]}
              alt="prev"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* CENTER IMAGE (large main) */}
          <Box
            sx={{
              position: "relative",
              width: 460,
              height: 400,
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
              zIndex: 2,
              transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.9)",
              },
            }}
          >
            <Box
              component="img"
              src={images[currentIndex]}
              alt="center"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Image counter */}
            <Box
              sx={{
                position: "absolute",
                bottom: 16,
                right: 16,
                bgcolor: "rgba(0,0,0,0.6)",
                color: "#fff",
                px: 2,
                py: 0.5,
                borderRadius: 20,
                fontSize: "0.85rem",
                backdropFilter: "blur(8px)",
              }}
            >
              {currentIndex + 1} / {images.length}
            </Box>
          </Box>

          {/* NEXT IMAGE (right small) */}
          <Box
            sx={{
              position: "absolute",
              right: "5%",
              width: 180,
              height: 260,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
              transform: "scale(0.8) perspective(1000px) rotateY(-15deg)",
              opacity: 0.6,
              transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: 1,
              filter: "brightness(0.7)",
              "&:hover": {
                transform: "scale(0.85) perspective(1000px) rotateY(-10deg)",
                opacity: 0.8,
              },
            }}
          >
            <Box
              component="img"
              src={images[getNextIndex(currentIndex)]}
              alt="next"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* ===== NAVIGATION ARROWS ===== */}
          <IconButton
            onClick={handlePrev}
            sx={{
              position: "absolute",
              left: { xs: 8, md: 20 },
              zIndex: 3,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(8px)",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.8)",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
              width: 48,
              height: 48,
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: 20, ml: 0.5 }} />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              right: { xs: 8, md: 20 },
              zIndex: 3,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(8px)",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.8)",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
              width: 48,
              height: 48,
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* ===== DOTS INDICATORS ===== */}
          <Box
            sx={{
              position: "absolute",
              bottom: -50,
              display: "flex",
              gap: 1.5,
              zIndex: 3,
            }}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                sx={{
                  width: idx === currentIndex ? 28 : 10,
                  height: 10,
                  borderRadius: 20,
                  bgcolor:
                    idx === currentIndex
                      ? "linear-gradient(135deg, #4CAF50, #2196F3)"
                      : "rgba(255,255,255,0.3)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.6)",
                    transform: "scale(1.1)",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
        {/* ================= END SLIDER ================= */}
      </Container>
    </Box>
  );
}
